
/** Arabic-Indic numerals (Unicode U+0660–U+0669) */
const ARABIC_NUMERALS = '٠١٢٣٤٥٦٧٨٩';

/** English numerals (0-9) */
const ENGLISH_NUMERALS = '0123456789';

/**
 * Arabic thousands (U+066C) and decimal (U+066B) separators, keyed by their Latin counterpart.
 *
 * @remarks
 * These are what `Intl.NumberFormat('ar-SA')` emits — `٧٬٠٨٣٫٢٦` — so mapping them here is
 * what keeps a hand-transliterated string identical to a locale-formatted one.
 */
const ARABIC_SEPARATORS: Record<string, string> = {
    ',': '٬',
    '.': '٫',
};

/**
 * Matches a `.` or `,` only when a digit sits on both sides of it.
 *
 * @remarks
 * The lookarounds are the whole point: a separator is only a separator between digits.
 * Without them `Price: 99.` and a `...` ellipsis would pick up Arabic decimal marks, and any
 * prose passed through {@link toArabicNumerals} would be punctuated in the wrong script.
 * Dates are safe for the same reason — `03/09/2026` separates on `/`, which is not matched.
 */
const DIGIT_SEPARATOR_PATTERN = /(?<=[0-9])[.,](?=[0-9])/g;

/**
 * Truncates a text to the defined length with ellipsis if needed.
 * @param text - The full text
 * @param length - The maximum length of the text
 * @returns Truncated text string
 */
export function truncate(text: string, length: number = 10): string {

    // void
    if (!text || text == null) return "";

    // void
    if (length == null) {
        throw new Error('length is required');
    }

    // return truncated text
    return text.length > length ? text.slice(0, length) + '...' : text;
}

/**
 * Converts English digits (0-9) to Arabic-Indic numerals in a string or number,
 * along with any grouping and decimal separators standing between those digits.
 *
 * @param value - String or number containing digits
 * @returns String with digits and numeric separators replaced by their Arabic forms
 *
 * @remarks
 * Separators are converted so that a value formatted elsewhere and transliterated here comes
 * out identical to one `Intl.NumberFormat('ar-SA')` produced directly — `7,083.26` and
 * `٧٬٠٨٣٫٢٦` are the same number in the two scripts, and the app renders both side by side.
 * Only digit-flanked separators are touched (see {@link DIGIT_SEPARATOR_PATTERN}), so dates,
 * sentence punctuation and ellipses pass through untouched.
 *
 * The inverse is {@link toEnglishNumerals}.
 */
export function toArabicNumerals(value: string | number): string {

    // check if the value is a string or number
    if (typeof value !== 'string' && typeof value !== 'number') {
        throw new Error('value is not a string or number');
    }

    const str = String(value);
    return str
        .replace(DIGIT_SEPARATOR_PATTERN, (separator) => ARABIC_SEPARATORS[separator])
        .replace(/[0-9]/g, (digit) => ARABIC_NUMERALS[parseInt(digit, 10)]);
}

/**
 * Converts Arabic-Indic numerals to English numerals in a string or number,
 * along with the Arabic grouping and decimal separators.
 *
 * @param value - String or number containing Arabic-Indic numerals
 * @returns String with numerals and numeric separators replaced by their English forms
 *
 * @remarks
 * The separators are replaced unconditionally, unlike in {@link toArabicNumerals}: `٬` and
 * `٫` carry no meaning outside a number, so there is nothing to guard against. That makes the
 * two functions exact inverses, which is what lets a value survive repeated language switches.
 */
export function toEnglishNumerals(value: string | number): string {

    // check if the value is a string or number
    if (typeof value !== 'string' && typeof value !== 'number') {
        throw new Error('value is not a string or number');
    }

    const str = String(value);
    return str
        .replace(/٬/g, ',')
        .replace(/٫/g, '.')
        .replace(/[٠-٩]/g, (digit) => ENGLISH_NUMERALS[ARABIC_NUMERALS.indexOf(digit)]);
}

/**
 * Converts a string (including Arabic-Indic numerals) to a number.
 * @param value - String containing digits (English or Arabic-Indic)
 * @returns Parsed number, or NaN if the result is not a valid number
 */
export function convertToNumber(value: string): number {
    
    // check if the value is a string or number
    if (typeof value == null) {
        return NaN
    }

    if (typeof value === 'number') return value;

    return parseInt(value)
}